import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ValidatorFn, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, finalize, Observable, of, Subject, switchMap } from 'rxjs';
import { BoardNovaPoshtaService } from '../../../services/board-nova-poshta.service';
import { NpCabinet, NpWarehouse } from '../nova-poshta.types';
import {
  formatNpPhone,
  isNpApiKeyFormat,
  NP_CABINET_NAME_MAX_LENGTH,
  NpCityOption,
  toNpCityOption,
  withPicked,
} from '../nova-poshta.utils';

/** Errors are keys of `input_errors`, the way `app-input` shows them */
const notBlank: ValidatorFn = (control) => (String(control.value ?? '').trim() ? null : { incorrect: true });
const apiKey: ValidatorFn = (control) =>
  !control.value || isNpApiKeyFormat(control.value) ? null : { np_api_key: true };

/** API errors of a key shown under its input */
const KEY_ERRORS: Record<string, string> = {
  NP_INVALID_API_KEY: 'np_api_key_rejected',
  NP_CABINET_DUPLICATE_KEY: 'np_api_key_duplicate',
};

/**
 * Settings of the Nova Poshta application: the cabinets of the company, each with its own API key and the branch the
 * shipments go from. A key is checked with Nova Poshta on save and the sender of the cabinet comes from it.
 */
@Component({
  selector: 'app-nova-poshta-settings',
  templateUrl: './nova-poshta-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full' },
})
export class NovaPoshtaSettingsComponent implements OnInit {
  /** Closes with the cabinets there are now */
  @Output() close = new EventEmitter<NpCabinet[] | void>();

  private readonly npService = inject(BoardNovaPoshtaService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(NonNullableFormBuilder);

  protected readonly form = this.fb.group({
    name: ['', [Validators.required, notBlank, Validators.maxLength(NP_CABINET_NAME_MAX_LENGTH)]],
    api_key: ['', apiKey],
    is_default: [false],
    /** Nova Poshta has no default branch of a sender: without it there is no waybill */
    sender_city_ref: this.fb.control<string | null>(null, Validators.required),
    sender_warehouse_ref: this.fb.control<string | null>(null, Validators.required),
  });

  protected cityOptions: NpCityOption[] = [];
  protected warehouseOptions: NpWarehouse[] = [];
  protected citiesLoading = false;
  protected warehousesLoading = false;
  private readonly citySearch$ = new Subject<string>();
  private readonly warehouseSearch$ = new Subject<string>();
  private pickedCity: NpCityOption | null = null;
  private pickedWarehouse: NpWarehouse | null = null;

  protected cabinets: NpCabinet[] = [];
  protected loading = true;
  protected saving = false;
  /** The cabinet whose request is running */
  protected busyId: string | null = null;
  /** The form is open: for a new cabinet when `editing` is null */
  protected formOpen = false;
  protected editing: NpCabinet | null = null;

  protected readonly nameMaxLength = NP_CABINET_NAME_MAX_LENGTH;
  protected readonly formatNpPhone = formatNpPhone;

  ngOnInit(): void {
    this.watchLookups();
    this.load(true);
  }

  protected onCitySearch(search: string): void {
    this.citySearch$.next(search);
  }

  protected onWarehouseSearch(search: string): void {
    this.warehouseSearch$.next(search);
  }

  protected onCityPicked(ref: string | null): void {
    this.pickedCity = this.cityOptions.find((city) => city.ref === ref) ?? null;
    this.pickedWarehouse = null;
    this.warehouseOptions = [];
    this.form.controls.sender_warehouse_ref.setValue(null);
    this.warehouseSearch$.next('');
  }

  protected onWarehousePicked(ref: string | null): void {
    this.pickedWarehouse = this.warehouseOptions.find((warehouse) => warehouse.ref === ref) ?? null;
  }

  protected openForm(cabinet: NpCabinet | null): void {
    this.editing = cabinet;
    this.formOpen = true;
    this.form.reset({
      name: cabinet?.name ?? (this.cabinets.length ? '' : this.npService.t('novaposhta.settings.default_name')),
      api_key: '',
      is_default: cabinet?.is_default ?? !this.cabinets.length,
      sender_city_ref: cabinet?.sender?.city?.ref ?? null,
      sender_warehouse_ref: cabinet?.sender?.warehouse?.ref ?? null,
    });
    const city = cabinet?.sender?.city;
    this.pickedCity = city ? toNpCityOption(city) : null;
    this.pickedWarehouse = cabinet?.sender?.warehouse ?? null;
    this.cityOptions = this.pickedCity ? [this.pickedCity] : [];
    this.warehouseOptions = this.pickedWarehouse ? [this.pickedWarehouse] : [];
    if (city) this.warehouseSearch$.next('');
    // The default cabinet stops being one only when another one becomes the default
    if (cabinet?.is_default) this.form.controls.is_default.disable();
    else this.form.controls.is_default.enable();
    // A new cabinet needs a key, a saved one keeps its key when the input is empty
    const validators = cabinet ? [apiKey] : [Validators.required, apiKey];
    this.form.controls.api_key.setValidators(validators);
    this.form.controls.api_key.updateValueAndValidity();
  }

  protected closeForm(): void {
    this.formOpen = false;
    this.editing = null;
  }

  protected save(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const name = value.name.trim();
    const key = value.api_key.trim();
    const editing = this.editing;
    const fields = {
      name,
      is_default: value.is_default,
      sender_city_ref: value.sender_city_ref,
      sender_warehouse_ref: value.sender_warehouse_ref,
    };
    const save$: Observable<NpCabinet> = editing
      ? this.npService.updateCabinet(editing.id, { ...fields, ...(key ? { api_key: key } : {}) })
      : this.npService.createCabinet({ ...fields, api_key: key });

    this.saving = true;
    save$.pipe(finalize(() => (this.saving = false))).subscribe({
      next: (cabinet) => {
        this.npService.success(editing ? 'novaposhta.settings.saved' : 'novaposhta.settings.added', {
          name: cabinet.name,
        });
        this.closeForm();
        this.load();
      },
      error: (error) => this.onSaveError(error),
    });
  }

  protected makeDefault(cabinet: NpCabinet): void {
    this.run(cabinet, this.npService.updateCabinet(cabinet.id, { is_default: true }));
  }

  protected remove(cabinet: NpCabinet): void {
    this.npService
      .confirm('novaposhta.settings.delete_confirm', { name: cabinet.name })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.run(cabinet, this.npService.deleteCabinet(cabinet.id)));
  }

  private run(cabinet: NpCabinet, change$: Observable<unknown>): void {
    this.busyId = cabinet.id;
    change$.pipe(finalize(() => (this.busyId = null))).subscribe({
      next: () => this.load(),
      error: (error) => this.npService.error(error),
    });
  }

  private onSaveError(error: unknown): void {
    const keyError = KEY_ERRORS[this.npService.getErrorCode(error) ?? ''];
    if (!keyError) {
      this.npService.error(error);
      return;
    }
    const control = this.form.controls.api_key;
    control.setErrors({ [keyError]: true });
    control.markAsTouched();
  }

  /** City search; the branches of the picked city, without postomats */
  private watchLookups(): void {
    this.citySearch$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((search) => {
          if (search.trim().length < 2) return of(null);
          this.citiesLoading = true;
          return this.npService.searchCities(search).pipe(
            catchError((error) => this.lookupFailed(error, null)),
            finalize(() => (this.citiesLoading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((cities) => {
        if (cities) this.cityOptions = withPicked(cities.map(toNpCityOption), this.pickedCity);
      });

    this.warehouseSearch$
      .pipe(
        debounceTime(200),
        switchMap((search) => {
          const cityRef = this.form.controls.sender_city_ref.value;
          if (!cityRef) return of([]);
          this.warehousesLoading = true;
          return this.npService.getWarehouses(cityRef, search, 'not_postomat').pipe(
            catchError((error) => this.lookupFailed(error, [])),
            finalize(() => (this.warehousesLoading = false)),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((warehouses) => (this.warehouseOptions = withPicked(warehouses, this.pickedWarehouse)));
  }

  /** A failed search keeps the next ones going */
  private lookupFailed<T>(error: unknown, fallback: T): Observable<T> {
    this.npService.error(error);
    return of(fallback);
  }

  /** `first`: right after the install there is nothing to look at but the form of the first cabinet */
  private load(first = false): void {
    this.npService
      .getCabinets()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (cabinets) => {
          this.cabinets = cabinets;
          if (first && !cabinets.length) this.openForm(null);
        },
        error: (error) => this.npService.error(error),
      });
  }

  protected done(): void {
    this.close.emit(this.cabinets);
  }
}
